import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { withRouter } from "react-router-dom";

import { TabSkeleton } from "@pages/casino/components";
import { BettoProviderModal, IFeelLuckyModal } from "@pages/casino/containers";
import { openCasinoSearchModal } from "@pages/casino/utils/bettoCasinoEvents";
import {
	buildContainerViewAllLink,
	isContainerFilterItemActive,
} from "@pages/casino/utils/bettoContainers";
import {
	clearBettoCasinoBrowseFilters,
	isBettoCasinoLobbyActive,
	isBettoFavoritesActive,
	isBettoHomeActive,
} from "@pages/casino/utils/bettoProviderFilter";

import useController from "../useController";
import { allGamesIcon, homeIcon } from "./categoryIcons";
import {
	BettoFilterIcon,
	BettoFilterPill,
	BettoFilterScroll,
	BettoFilterTrack,
	BettoFiltersRoot,
	BettoScrollButton,
	BettoScrollButtonLeft,
	BettoScrollFade,
	BettoScrollFadeLeft,
	BettoSearchBar,
	BettoSearchIcon,
	BettoSearchPlaceholder,
	searchIcon,
} from "./elements";

import casinoLobbyIcon from "@layouts/betto/assets/icons/casino.svg";
import liveCasinoLobbyIcon from "@layouts/betto/assets/icons/live-casino.svg";

const BettoGameFilters = ({
	filterValues,
	activeTab,
	showSearch,
	showProvidersFilter,
	showCategoriesFilter,
	showContainerCategories,
	showIFeelLucky,
	showAllGames,
	showHomeFilter = false,
	showLobbyFilter = window.TENANT === "betto",
	history,
	location,
	onClose,
	onSelectFilter,
	onlySelectFilter,
}) => {
	const scrollRef = useRef(null);
	const favoritesPillRef = useRef(null);
	const [isScrollable, setIsScrollable] = useState(false);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const {
		helpers: { translate },
		state: { isContainerLoading, categories, containerCategories, affiliates },
		handlers: { handleAllGamesButtonClick, handleProviderSelect },
	} = useController({
		history,
		filterValues,
		onSelectFilter,
		activeTab,
		onlySelectFilter,
		showCategoriesFilter,
		showContainerCategories,
		showProvidersFilter,
	});

	const selectedProvider = filterValues?.find((item) => item?.section === "provider");

	const searchParams = useMemo(
		() => new URLSearchParams(location?.search ?? ""),
		[location?.search]
	);
	const activeBrowseKey = searchParams.get("key");
	const isAllGamesActive = activeBrowseKey === "allGames";

	const isLiveCasinoTab = activeTab?.toLowerCase() === "livecasino";
	const lobbyPage = isLiveCasinoTab ? "livecasino" : "casino";
	const lobbyIcon = isLiveCasinoTab ? liveCasinoLobbyIcon : casinoLobbyIcon;
	const isLobbyActive = isBettoCasinoLobbyActive(location, lobbyPage);
	const isHomeActive = isBettoHomeActive(location);
	const isFavoritesActive = isBettoFavoritesActive(location);

	const handleHomeClick = useCallback(() => {
		history.push("/");
	}, [history]);

	const handleLobbyClick = useCallback(() => {
		clearBettoCasinoBrowseFilters();
		history.push(`/casino?page=${lobbyPage}`);
	}, [history, lobbyPage]);

	const handleContainerClick = useCallback(
		(container) => {
			if (isContainerFilterItemActive(container, location)) {
				clearBettoCasinoBrowseFilters();
				history.push(`/casino?page=${lobbyPage}`);
				return;
			}
			const tab = isLiveCasinoTab ? "livecasino" : "casino";
			const query = buildContainerViewAllLink(
				container.container,
				tab,
				container.containerPage
			);
			history.push(query);
		},
		[history, isLiveCasinoTab, location, lobbyPage]
	);

	const updateScrollState = useCallback(() => {
		const el = scrollRef.current;
		if (!el) {
			setIsScrollable(false);
			setCanScrollLeft(false);
			setCanScrollRight(false);
			return;
		}

		const hasOverflow = el.scrollWidth > el.clientWidth + 2;
		setIsScrollable(hasOverflow);
		setCanScrollLeft(hasOverflow && el.scrollLeft > 16);
		setCanScrollRight(hasOverflow && el.scrollLeft + el.clientWidth < el.scrollWidth - 16);
	}, []);

	useEffect(() => {
		updateScrollState();
		const el = scrollRef.current;
		if (!el) return undefined;

		el.addEventListener("scroll", updateScrollState, { passive: true });
		window.addEventListener("resize", updateScrollState);

		return () => {
			el.removeEventListener("scroll", updateScrollState);
			window.removeEventListener("resize", updateScrollState);
		};
	}, [categories, containerCategories, affiliates, updateScrollState]);

	const scrollPillIntoView = useCallback(
		(pillRef) => {
			const pill = pillRef?.current;
			if (!pill || typeof pill.scrollIntoView !== "function") return;

			pill.scrollIntoView({ behavior: "smooth", inline: "end", block: "nearest" });
			requestAnimationFrame(updateScrollState);
		},
		[updateScrollState]
	);

	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;

		if (isFavoritesActive) {
			scrollPillIntoView(favoritesPillRef);
			return;
		}

		el.scrollLeft = 0;
		updateScrollState();
	}, [
		location?.search,
		location?.pathname,
		isFavoritesActive,
		scrollPillIntoView,
		updateScrollState,
	]);

	useEffect(() => {
		if (!isFavoritesActive || isContainerLoading) return;

		const frameId = requestAnimationFrame(() => {
			scrollPillIntoView(favoritesPillRef);
		});

		return () => cancelAnimationFrame(frameId);
	}, [isFavoritesActive, isContainerLoading, scrollPillIntoView]);

	useEffect(() => {
		if (searchParams.get("openProviders") === "true") {
			const t = setTimeout(() => {
				window.dispatchEvent(new CustomEvent("openCasinoProviderModal"));
			}, 200);
			return () => clearTimeout(t);
		}
	}, [location?.search, searchParams]);

	const handleScrollRight = useCallback(() => {
		scrollRef.current?.scrollBy({ left: 220, behavior: "smooth" });
	}, []);

	const handleScrollLeft = useCallback(() => {
		scrollRef.current?.scrollBy({ left: -220, behavior: "smooth" });
	}, []);

	return (
		<BettoFiltersRoot id="casino-filters-container">
			{showSearch && (
				<>
					<BettoSearchBar
						type="button"
						data-testid="button-casino-filter-search-game"
						aria-label={translate("casino.gameSearch")}
						onClick={() => openCasinoSearchModal()}>
						<BettoSearchIcon
							src={searchIcon}
							alt=""
						/>
						<BettoSearchPlaceholder>
							{translate("betto.casino.searchGames")}
						</BettoSearchPlaceholder>
					</BettoSearchBar>
				</>
			)}

			<BettoFilterTrack>
				<BettoFilterScroll
					innerRef={scrollRef}
					$hasScrollPadding={isScrollable}>
					{showHomeFilter && (
						<BettoFilterPill
							type="button"
							$active={isHomeActive}
							data-testid="button-casino-filter-home"
							onClick={handleHomeClick}>
							<BettoFilterIcon
								src={homeIcon}
								alt=""
							/>
							<span>{translate("betto.casino.home")}</span>
						</BettoFilterPill>
					)}

					{showLobbyFilter && (
						<BettoFilterPill
							type="button"
							$active={isLobbyActive}
							data-testid="button-casino-filter-lobby"
							onClick={handleLobbyClick}>
							<BettoFilterIcon
								src={lobbyIcon}
								alt=""
							/>
							<span>{translate("betto.casino.lobby")}</span>
						</BettoFilterPill>
					)}

					{showAllGames && (
						<BettoFilterPill
							type="button"
							$active={isAllGamesActive}
							data-testid="button-casino-filter-all-games"
							onClick={() => {
								if (isAllGamesActive) {
									clearBettoCasinoBrowseFilters();
									history.push(`/casino?page=${lobbyPage}`);
								} else {
									handleAllGamesButtonClick();
								}
							}}>
							<BettoFilterIcon
								src={allGamesIcon}
								alt=""
							/>
							<span>{translate("betto.casino.allGames")}</span>
						</BettoFilterPill>
					)}

					{showContainerCategories &&
						(isContainerLoading ? (
							<TabSkeleton />
						) : (
							containerCategories.map((container) => (
								<BettoFilterPill
									key={`container-pill-${container.key}`}
									type="button"
									$active={isContainerFilterItemActive(container, location)}
									data-testid={`button-casino-filter-container-${container.key}`}
									onClick={() => handleContainerClick(container)}>
									{container.iconUrl && (
										<BettoFilterIcon
											src={container.iconUrl}
											alt=""
										/>
									)}
									<span>{container.value}</span>
								</BettoFilterPill>
							))
						))}

					{showProvidersFilter && (
						<BettoProviderModal
							data-testid={
								onlySelectFilter
									? "button-filtered-casino-providers"
									: "button-casino-filter-providers"
							}
							onClose={onClose}
							placeholder={translate("betto.casino.providers")}
							items={affiliates}
							value={selectedProvider}
							onSelect={handleProviderSelect}
						/>
					)}

					{showIFeelLucky && <IFeelLuckyModal labelKey="betto.casino.iFeelLucky" />}
				</BettoFilterScroll>

				{isScrollable && canScrollLeft && <BettoScrollFadeLeft aria-hidden />}
				{isScrollable && canScrollRight && <BettoScrollFade aria-hidden />}
				{isScrollable && canScrollLeft && (
					<BettoScrollButtonLeft
						type="button"
						aria-label="Scroll filters left"
						onClick={handleScrollLeft}
					/>
				)}
				{isScrollable && canScrollRight && (
					<BettoScrollButton
						type="button"
						aria-label="Scroll filters"
						onClick={handleScrollRight}
					/>
				)}
			</BettoFilterTrack>
		</BettoFiltersRoot>
	);
};

export default withRouter(BettoGameFilters);
